/**
 * This file was auto-generated by Fern from our API Definition.
 */

import * as core from '../../../../core';
import { mergeHeaders, mergeOnlyDefinedHeaders } from '../../../../core/headers';
import * as environments from '../../../../environments';
import * as errors from '../../../../errors/index';
import * as serializers from '../../../../serialization/index';
import * as Truvity from '../../../index';

export declare namespace Drafts {
    export interface Options {
        environment?: core.Supplier<environments.TruvityEnvironment | string>;
        /** Specify a custom URL to connect the client to. */
        baseUrl?: core.Supplier<string>;
        apiKey?: core.Supplier<string | undefined>;
        /** Additional headers to include in requests. */
        headers?: Record<string, string | core.Supplier<string | undefined> | undefined>;
        fetcher?: core.FetchFunction;
    }

    export interface RequestOptions {
        /** The maximum time to wait for a response in seconds. */
        timeoutInSeconds?: number;
        /** The number of times to retry the request. Defaults to 2. */
        maxRetries?: number;
        /** A hook to abort the request. */
        abortSignal?: AbortSignal;
        /** Additional query string parameters to include in the request. */
        queryParams?: Record<string, unknown>;
        /** Additional headers to include in the request. */
        headers?: Record<string, string | core.Supplier<string | undefined> | undefined>;
    }
}

/**
 * The API to manage Drafts.
 */
export class Drafts {
    protected readonly _options: Drafts.Options;

    constructor(_options: Drafts.Options = {}) {
        this._options = _options;
    }

    /**
     * Creates Draft's resource.
     *
     * @param {Truvity.DraftCreateRequest} request
     * @param {Drafts.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.UnsupportedMediaTypeError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.drafts.draftCreate({
     *         data: {
     *             metaSchema: {
     *                 schema: {
     *                     fields: [{
     *                             kind: "BOOLEAN",
     *                             name: "name"
     *                         }]
     *                 }
     *             }
     *         }
     *     })
     */
    public draftCreate(
        request: Truvity.DraftCreateRequest,
        requestOptions?: Drafts.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceDraft> {
        return core.HttpResponsePromise.fromPromise(this.__draftCreate(request, requestOptions));
    }

    private async __draftCreate(
        request: Truvity.DraftCreateRequest,
        requestOptions?: Drafts.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceDraft>> {
        const { idempotencyKey, ..._body } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                'drafts',
            ),
            method: 'POST',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.DraftCreateRequest.jsonOrThrow(_body, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceDraft.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 415:
                    throw new Truvity.UnsupportedMediaTypeError(
                        serializers.UnsupportedMediaTypeProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling POST /drafts.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Search for Drafts with extended filters capabilities.
     *
     * @param {Truvity.DraftSearchRequest} request
     * @param {Drafts.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.UnsupportedMediaTypeError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.drafts.draftSearch()
     */
    public draftSearch(
        request: Truvity.DraftSearchRequest = {},
        requestOptions?: Drafts.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ListDraft> {
        return core.HttpResponsePromise.fromPromise(this.__draftSearch(request, requestOptions));
    }

    private async __draftSearch(
        request: Truvity.DraftSearchRequest = {},
        requestOptions?: Drafts.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ListDraft>> {
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({ ...(await this._getCustomAuthorizationHeaders()) }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                'drafts/search',
            ),
            method: 'POST',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.DraftSearchRequest.jsonOrThrow(request, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ListDraft.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 415:
                    throw new Truvity.UnsupportedMediaTypeError(
                        serializers.UnsupportedMediaTypeProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling POST /drafts/search.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Get the latest version of Draft.
     *
     * @param {string} id - The ID of the resource.
     * @param {Truvity.DraftLatestRequest} request
     * @param {Drafts.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.drafts.draftLatest("id")
     */
    public draftLatest(
        id: string,
        request: Truvity.DraftLatestRequest = {},
        requestOptions?: Drafts.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceDraft> {
        return core.HttpResponsePromise.fromPromise(this.__draftLatest(id, request, requestOptions));
    }

    private async __draftLatest(
        id: string,
        request: Truvity.DraftLatestRequest = {},
        requestOptions?: Drafts.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceDraft>> {
        const { ifNoneMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'If-None-Match': ifNoneMatch != null ? ifNoneMatch : undefined,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `drafts/${encodeURIComponent(id)}`,
            ),
            method: 'GET',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceDraft.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling GET /drafts/{id}.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Delete Draft's resource.
     *
     * @param {string} id - The primary and unique identifier of the resource (inside tenant)
     *                      according to [RFC 4122](https://www.rfc-editor.org/info/rfc4122).
     * @param {Truvity.DraftDeleteRequest} request
     * @param {Drafts.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.PreconditionFailedError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.drafts.draftDelete("id", {
     *         ifMatch: "If-Match"
     *     })
     */
    public draftDelete(
        id: string,
        request: Truvity.DraftDeleteRequest,
        requestOptions?: Drafts.RequestOptions,
    ): core.HttpResponsePromise<void> {
        return core.HttpResponsePromise.fromPromise(this.__draftDelete(id, request, requestOptions));
    }

    private async __draftDelete(
        id: string,
        request: Truvity.DraftDeleteRequest,
        requestOptions?: Drafts.RequestOptions,
    ): Promise<core.WithRawResponse<void>> {
        const { idempotencyKey, ifMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                'If-Match': ifMatch,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `drafts/${encodeURIComponent(id)}`,
            ),
            method: 'DELETE',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return { data: undefined, rawResponse: _response.rawResponse };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 412:
                    throw new Truvity.PreconditionFailedError(
                        serializers.PreconditionFailedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling DELETE /drafts/{id}.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Update Draft's resource.
     *
     * @param {string} id - The primary and unique identifier of the resource (inside tenant)
     *                      according to [RFC 4122](https://www.rfc-editor.org/info/rfc4122).
     * @param {Truvity.DraftUpdateRequest} request
     * @param {Drafts.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.PreconditionFailedError}
     * @throws {@link Truvity.UnsupportedMediaTypeError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.drafts.draftUpdate("id", {
     *         ifMatch: "If-Match",
     *         data: {}
     *     })
     */
    public draftUpdate(
        id: string,
        request: Truvity.DraftUpdateRequest,
        requestOptions?: Drafts.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceDraft> {
        return core.HttpResponsePromise.fromPromise(this.__draftUpdate(id, request, requestOptions));
    }

    private async __draftUpdate(
        id: string,
        request: Truvity.DraftUpdateRequest,
        requestOptions?: Drafts.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceDraft>> {
        const { idempotencyKey, ifMatch, ..._body } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                'If-Match': ifMatch,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `drafts/${encodeURIComponent(id)}`,
            ),
            method: 'PATCH',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.DraftUpdateRequest.jsonOrThrow(_body, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceDraft.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 412:
                    throw new Truvity.PreconditionFailedError(
                        serializers.PreconditionFailedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 415:
                    throw new Truvity.UnsupportedMediaTypeError(
                        serializers.UnsupportedMediaTypeProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling PATCH /drafts/{id}.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Update annotations of Draft's resource.
     *
     * @param {string} id - The primary and unique identifier of the resource (inside tenant)
     *                      according to [RFC 4122](https://www.rfc-editor.org/info/rfc4122).
     * @param {Truvity.DraftUpdateAnnotationsRequest} request
     * @param {Drafts.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.PreconditionFailedError}
     * @throws {@link Truvity.UnsupportedMediaTypeError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.drafts.draftUpdateAnnotations("id", {
     *         ifMatch: "If-Match",
     *         annotations: {
     *             "key": "value"
     *         }
     *     })
     */
    public draftUpdateAnnotations(
        id: string,
        request: Truvity.DraftUpdateAnnotationsRequest,
        requestOptions?: Drafts.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceDraft> {
        return core.HttpResponsePromise.fromPromise(this.__draftUpdateAnnotations(id, request, requestOptions));
    }

    private async __draftUpdateAnnotations(
        id: string,
        request: Truvity.DraftUpdateAnnotationsRequest,
        requestOptions?: Drafts.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceDraft>> {
        const { idempotencyKey, ifMatch, ..._body } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                'If-Match': ifMatch,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `drafts/${encodeURIComponent(id)}/annotations`,
            ),
            method: 'PATCH',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.DraftUpdateAnnotationsRequest.jsonOrThrow(_body, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceDraft.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 412:
                    throw new Truvity.PreconditionFailedError(
                        serializers.PreconditionFailedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 415:
                    throw new Truvity.UnsupportedMediaTypeError(
                        serializers.UnsupportedMediaTypeProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling PATCH /drafts/{id}/annotations.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * This operation issues a Verifiable Credential from a Draft resource. Operational Lifecycle:
     *    1) [Exporting](#tag/drafts/operation/DraftExport) Draft.
     *    2) [Signing](#tag/keys/operation/CredentialSign) the exported Draft.
     *    3) [Importing](#tag/credentials/operation/CredentialImport) the signed Credential.
     *
     * Cascade Issuance: If the Draft references other drafts the system automatically builds a dependency graph and issues all required drafts in the correct order.
     * The correct order is a topological order of the dependency graph, where each draft is issued only after all its prerequisite drafts have been issued.
     *
     * Strict Mode:
     * - The `strict_mode` option allows clients to control how prerequisite drafts are handled during cascade issuance.
     * - Use `strict_mode` to enforce that only the explicitly specified drafts in `prerequisite_drafts` (with matching ETags) are accepted as prerequisites.
     * - When disabled (default), the system will automatically discover and include all linked drafts as prerequisites, building the full dependency graph for cascade issuance.
     *
     * Performance Considerations:
     * - The process may take longer than typical API calls due to:
     * - Building and traversing the dependency graph for cascade issuance, which determines the correct issuance order for all linked drafts.
     * - Fetching and resolving external resources required for generating cryptographic proofs, which may involve network requests.
     * - Generating cryptographic proofs for each credential in the dependency graph.
     *
     * Concurrency and Consistency:
     * - If the `If-Match` header is provided, strict version control is enforced. The operation will fail if the ETag does not match the current Draft version.
     * - The endpoint is idempotent with respect to the Draft resource and ETag.
     *
     * Recovery:
     * - If the issuance process fails at any stage, the operation can be safely retried. The endpoint is idempotent with respect to the Draft resource and ETag, so repeated calls will not result in duplicate credentials or inconsistent state.
     *
     * @param {string} id - The ID of the resource.
     * @param {Truvity.DraftLatestIssueRequest} request
     * @param {Drafts.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.PreconditionFailedError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     * @throws {@link Truvity.ServiceUnavailableError}
     *
     * @example
     *     await client.drafts.draftLatestIssue("id", {
     *         keyId: "key_id"
     *     })
     */
    public draftLatestIssue(
        id: string,
        request: Truvity.DraftLatestIssueRequest,
        requestOptions?: Drafts.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceCredential> {
        return core.HttpResponsePromise.fromPromise(this.__draftLatestIssue(id, request, requestOptions));
    }

    private async __draftLatestIssue(
        id: string,
        request: Truvity.DraftLatestIssueRequest,
        requestOptions?: Drafts.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceCredential>> {
        const { ifMatch, ..._body } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'If-Match': ifMatch != null ? ifMatch : undefined,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `drafts/${encodeURIComponent(id)}/issue`,
            ),
            method: 'PATCH',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.DraftLatestIssueRequest.jsonOrThrow(_body, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceCredential.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 412:
                    throw new Truvity.PreconditionFailedError(
                        serializers.PreconditionFailedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 503:
                    throw new Truvity.ServiceUnavailableError(
                        serializers.ServiceUnavailableProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling PATCH /drafts/{id}/issue.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Update labels of Draft's resource.
     *
     * @param {string} id - The primary and unique identifier of the resource (inside tenant)
     *                      according to [RFC 4122](https://www.rfc-editor.org/info/rfc4122).
     * @param {Truvity.DraftUpdateLabelsRequest} request
     * @param {Drafts.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.PreconditionFailedError}
     * @throws {@link Truvity.UnsupportedMediaTypeError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.drafts.draftUpdateLabels("id", {
     *         ifMatch: "If-Match",
     *         labels: {
     *             "key": "value"
     *         }
     *     })
     */
    public draftUpdateLabels(
        id: string,
        request: Truvity.DraftUpdateLabelsRequest,
        requestOptions?: Drafts.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceDraft> {
        return core.HttpResponsePromise.fromPromise(this.__draftUpdateLabels(id, request, requestOptions));
    }

    private async __draftUpdateLabels(
        id: string,
        request: Truvity.DraftUpdateLabelsRequest,
        requestOptions?: Drafts.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceDraft>> {
        const { idempotencyKey, ifMatch, ..._body } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                'If-Match': ifMatch,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `drafts/${encodeURIComponent(id)}/labels`,
            ),
            method: 'PATCH',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.DraftUpdateLabelsRequest.jsonOrThrow(_body, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceDraft.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 412:
                    throw new Truvity.PreconditionFailedError(
                        serializers.PreconditionFailedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 415:
                    throw new Truvity.UnsupportedMediaTypeError(
                        serializers.UnsupportedMediaTypeProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling PATCH /drafts/{id}/labels.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Restore Draft's resource.
     *
     * @param {string} id - The primary and unique identifier of the resource (inside tenant)
     *                      according to [RFC 4122](https://www.rfc-editor.org/info/rfc4122).
     * @param {Truvity.DraftRestoreRequest} request
     * @param {Drafts.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.PreconditionFailedError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.drafts.draftRestore("id", {
     *         ifMatch: "If-Match"
     *     })
     */
    public draftRestore(
        id: string,
        request: Truvity.DraftRestoreRequest,
        requestOptions?: Drafts.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceDraft> {
        return core.HttpResponsePromise.fromPromise(this.__draftRestore(id, request, requestOptions));
    }

    private async __draftRestore(
        id: string,
        request: Truvity.DraftRestoreRequest,
        requestOptions?: Drafts.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceDraft>> {
        const { idempotencyKey, ifMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                'If-Match': ifMatch,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `drafts/${encodeURIComponent(id)}/restore`,
            ),
            method: 'POST',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceDraft.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 412:
                    throw new Truvity.PreconditionFailedError(
                        serializers.PreconditionFailedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling POST /drafts/{id}/restore.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * History of the Draft's resources.
     *
     * @param {string} id - The ID of the resource.
     * @param {Truvity.DraftHistoryRequest} request
     * @param {Drafts.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.drafts.draftHistory("id")
     */
    public draftHistory(
        id: string,
        request: Truvity.DraftHistoryRequest = {},
        requestOptions?: Drafts.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ListDraft> {
        return core.HttpResponsePromise.fromPromise(this.__draftHistory(id, request, requestOptions));
    }

    private async __draftHistory(
        id: string,
        request: Truvity.DraftHistoryRequest = {},
        requestOptions?: Drafts.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ListDraft>> {
        const { ifNoneMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'If-None-Match': ifNoneMatch != null ? ifNoneMatch : undefined,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `drafts/${encodeURIComponent(id)}/revisions`,
            ),
            method: 'GET',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ListDraft.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling GET /drafts/{id}/revisions.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Read the specific version of the Draft's resource.
     *
     * @param {string} id - The ID of the resource.
     * @param {number} revision - The revision of the resource.
     * @param {Truvity.DraftRevisionRequest} request
     * @param {Drafts.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.drafts.draftRevision("id", 1)
     */
    public draftRevision(
        id: string,
        revision: number,
        request: Truvity.DraftRevisionRequest = {},
        requestOptions?: Drafts.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourceDraft> {
        return core.HttpResponsePromise.fromPromise(this.__draftRevision(id, revision, request, requestOptions));
    }

    private async __draftRevision(
        id: string,
        revision: number,
        request: Truvity.DraftRevisionRequest = {},
        requestOptions?: Drafts.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourceDraft>> {
        const { ifNoneMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'If-None-Match': ifNoneMatch != null ? ifNoneMatch : undefined,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `drafts/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revision)}`,
            ),
            method: 'GET',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourceDraft.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError(
                    'Timeout exceeded when calling GET /drafts/{id}/revisions/{revision}.',
                );
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Generate an unsigned Verifiable Credential in JSON-LD format from a Draft.
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     */
    public draftExport(
        id: string,
        revision: number,
        request: Truvity.DraftExportRequest,
        requestOptions?: Drafts.RequestOptions,
    ): core.HttpResponsePromise<core.BinaryResponse> {
        return core.HttpResponsePromise.fromPromise(this.__draftExport(id, revision, request, requestOptions));
    }

    private async __draftExport(
        id: string,
        revision: number,
        request: Truvity.DraftExportRequest,
        requestOptions?: Drafts.RequestOptions,
    ): Promise<core.WithRawResponse<core.BinaryResponse>> {
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({ ...(await this._getCustomAuthorizationHeaders()) }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)<core.BinaryResponse>({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `drafts/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revision)}/export`,
            ),
            method: 'POST',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.DraftExportRequest.jsonOrThrow(request, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            responseType: 'binary-response',
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError(
                    'Timeout exceeded when calling POST /drafts/{id}/revisions/{revision}/export.',
                );
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    protected async _getCustomAuthorizationHeaders() {
        const apiKeyValue = await core.Supplier.get(this._options.apiKey);
        return { 'X-API-KEY': apiKeyValue };
    }
}
